/**
 * Create-form prefill + "stay after create" navigation contract for the
 * new-record screen (`/<type>/new?...`, rendered by NewResourceSingle).
 *
 * Two optional URL query parameters let a caller open the create form for a
 * custom object with some attribute values already set, and keep the user on
 * the screen they came from after saving:
 *
 * - `prefill` — a JSON object of initial custom-object `value` attributes,
 *   e.g. `{"parent": "<record id>"}`. Only keys that are attributes of the
 *   target object_template are applied (unknown keys are ignored), and the
 *   prefill is merged OVER the template defaults (incl. `before_create`
 *   defaults), so every other attribute keeps its default.
 * - `stay` — when `"1"`/`"true"`, a successful save returns to the previous
 *   screen instead of opening the newly created record.
 *
 * Used by the incoming-reference element's "Create <Template>" button
 * (operator-task-fe-incoming-reference-create-record-button-prefill-stay-refresh)
 * and generic enough for any "create a related record" affordance
 * (operator-task-fe-create-form-prefill-initial-attribute-values).
 */

export const CREATE_PREFILL_PARAM = "prefill";
export const CREATE_STAY_PARAM = "stay";

type PlainObject = Record<string, unknown>;

const isPlainObject = (value: unknown): value is PlainObject =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Parse the `prefill` query parameter into the initial attribute values to
 * apply, restricted to the attributes the object_template schema declares.
 *
 * Returns `{}` when the parameter is absent, is not valid JSON, is not a JSON
 * object, or when the schema does not (yet) declare any properties — the
 * schema arrives asynchronously, so callers recompute once it is available.
 */
export const parseCreatePrefill = (raw: unknown, schema: unknown): PlainObject => {
    if (typeof raw !== "string" || raw === "") {
        return {};
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return {};
    }
    if (!isPlainObject(parsed)) {
        return {};
    }
    const properties = isPlainObject(schema) ? schema.properties : undefined;
    if (!isPlainObject(properties)) {
        return {};
    }
    return Object.fromEntries(
        Object.entries(parsed).filter(([key]) =>
            Object.prototype.hasOwnProperty.call(properties, key)
        )
    );
};

/**
 * Merge prefill values over the form's initial custom-object value. Returns the
 * original object unchanged when there is nothing to apply.
 */
export const applyCreatePrefill = <T extends PlainObject | null | undefined>(
    values: T,
    prefill: PlainObject
): T | PlainObject =>
    Object.keys(prefill).length === 0 ? values : { ...(values ?? {}), ...prefill };

/** Whether the create screen should return to the previous screen after saving. */
export const isStayAfterCreate = (queryParams: Record<string, unknown>): boolean => {
    const value = queryParams?.[CREATE_STAY_PARAM];
    return value === "1" || value === "true";
};

/**
 * Build the extra query parameters for opening the custom-object create form
 * with a prefill (and, optionally, the stay-after-create behaviour).
 */
export const buildCreatePrefillParams = (
    prefill: PlainObject,
    { stay }: { stay: boolean }
): Record<string, string> => ({
    [CREATE_PREFILL_PARAM]: JSON.stringify(prefill),
    ...(stay ? { [CREATE_STAY_PARAM]: "1" } : {}),
});
